import { findEventEntry } from "@repo/vihangam-yog-shared";
import { ChevronDown } from "lucide-react";
import { useId } from "react";

import {
  adultsAndChildren,
  firstTimerLine,
  notComingLine,
  notPaidYetLine,
  whoIsComingParts,
} from "./guestFormat";
import { isNotComing, isSeva } from "./notComing";
import {
  accessCountsOf,
  firstTimerCount,
  NEW_WINDOW_MS,
  paidSummaryOf,
  sourceLabel,
  type EventGroup,
} from "./shared";

const LINE = "text-[16px] text-[color:var(--color-ink)]";

/** How people heard about the event, most common first. Empty when no
 *  one answered, so the list never reads only "Not given". */
function heardAboutUsCounts(group: EventGroup): [string, number][] {
  const regs = group.registrations;
  if (!regs.some((r) => r.heardAboutUs)) {
    return [];
  }
  const counts = new Map<string, number>();
  for (const r of regs) {
    const key = r.heardAboutUs ? sourceLabel(r.heardAboutUs) : "Not given";
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1]);
}

/**
 * "More about this event": the numbers that don't need to be on screen
 * all the time, closed until someone opens it. Each part shows only when
 * it has something in it; with nothing to show, there is no disclosure.
 */
export function MoreAboutEvent({
  group,
  now,
}: {
  group: EventGroup;
  now: number;
}) {
  const sourcesId = useId();
  const regs = group.registrations;
  // Who is coming leaves out bookings marked not coming (seva); they are
  // counted once, in a line of their own.
  const coming = regs.filter((r) => !isNotComing(r));
  const notComing = regs.filter((r) => isNotComing(r));
  const adults = coming.reduce((sum, r) => sum + r.adults, 0);
  const children = coming.reduce((sum, r) => sum + r.children, 0);
  const people = adultsAndChildren(adults, children);
  const notComingText = notComingLine(
    notComing.filter((r) => isSeva(r)).length,
    notComing.filter((r) => !isSeva(r)).length,
  );
  const fresh = regs.filter(
    (r) => now - new Date(r.createdAt).getTime() < NEW_WINDOW_MS,
  ).length;
  const paid = paidSummaryOf(regs);
  // A Hawan's Families figure is already the paid bookings.
  const isKundEvent = Boolean(
    findEventEntry(group.eventSlug)?.price?.kundsTotal,
  );
  const showPaidBookings = paid.paidCount > 0 && !isKundEvent;
  const who = whoIsComingParts(accessCountsOf(coming));
  const firstTimers = firstTimerCount(coming);
  const sources = heardAboutUsCounts(group);

  const hasAnything =
    people !== "" ||
    notComingText !== null ||
    fresh > 0 ||
    showPaidBookings ||
    paid.interestCount > 0 ||
    who.length > 0 ||
    firstTimers > 0 ||
    sources.length > 0;
  if (!hasAnything) {
    return null;
  }

  return (
    <details className="group mt-4 rounded-lg border border-[color:var(--color-line)]">
      {/* `flex` drops the summary's own triangle, so the chevron says it opens. */}
      <summary className="flex min-h-11 list-none items-center justify-between gap-3 px-4 py-2 text-[16px] font-semibold text-[color:var(--color-ink)] [&::-webkit-details-marker]:hidden">
        More about this event
        <ChevronDown
          size={18}
          aria-hidden="true"
          className="shrink-0 text-[color:var(--color-ink-soft)] transition-transform group-open:rotate-180"
        />
      </summary>
      <div className="space-y-2 px-4 pb-4">
        {people ? (
          <p className={LINE}>
            <span className="font-semibold">People coming:</span> {people}
          </p>
        ) : null}
        {notComingText ? <p className={LINE}>{notComingText}</p> : null}
        {fresh > 0 ? (
          <p className={LINE}>
            {fresh} new {fresh === 1 ? "booking" : "bookings"} in the last 2
            days
          </p>
        ) : null}
        {showPaidBookings ? (
          <p className={LINE}>
            {paid.paidCount} paid{" "}
            {paid.paidCount === 1 ? "booking" : "bookings"}
          </p>
        ) : null}
        {paid.interestCount > 0 ? (
          <p className={LINE}>{notPaidYetLine(paid.interestCount)}</p>
        ) : null}
        {who.length > 0 ? (
          <p className={LINE}>
            <span className="font-semibold">Who is coming:</span>{" "}
            {who.join(" · ")}
          </p>
        ) : null}
        {firstTimers > 0 ? (
          <p className={LINE}>{firstTimerLine(firstTimers)}</p>
        ) : null}
        {sources.length > 0 ? (
          <div className="pt-2">
            <p
              id={sourcesId}
              className="text-[16px] font-semibold text-[color:var(--color-ink)]"
            >
              How people heard about us
            </p>
            <ul
              aria-labelledby={sourcesId}
              className="mt-1 max-w-sm divide-y divide-[color:var(--color-line)] text-[16px]"
            >
              {sources.map(([label, count]) => (
                <li key={label} className="flex justify-between gap-4 py-2">
                  <span className="text-[color:var(--color-ink)]">{label}</span>
                  <span className="font-semibold text-[color:var(--color-ink)] tabular-nums">
                    {count}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </details>
  );
}
